import React, { useState } from 'react';
import { Alert, Box, Text, Button, Modal, Stack, Group, List } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useTranslation, Trans } from 'react-i18next';

interface MMEWarningModalProps {
    isOpen: boolean;
    onAccepted: () => Promise<void>;
}

export const MMEWarningModal: React.FC<MMEWarningModalProps> = ({ isOpen, onAccepted }) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleAccept = async () => {
        try {
            setIsLoading(true);
            setError(null);
            await onAccepted();
        } catch (err) {
            console.error('Failed to accept MME warning:', err);
            setError(t('mme.calculator.warningModal.error', 'Failed to save your acceptance. Please try again.'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            opened={isOpen}
            onClose={() => {}} // Prevent closing by clicking outside
            centered
            size="lg"
            withCloseButton={false}
            title={
                <Group gap="xs">
                    <IconAlertTriangle size={20} color="orange" />
                    <Text fw={600}>{t('mme.calculator.warningModal.title', 'Important Notice')}</Text>
                </Group>
            }
        >
            <Stack gap="md">
                <Alert color="yellow">
                    <Box size="sm" component="div" style={{ lineHeight: 1.6, fontSize: '14px' }}>
                        <Trans
                            i18nKey="mme.calculator.warningModal.message"
                            components={{
                                bold: <Text component="span" fw={700} />,
                                p: <Text component="p" mb="sm" />,
                                ul: <List size="sm" spacing="xs" mb="sm" />,
                                li: <List.Item />
                            }}
                        />
                    </Box>
                </Alert>

                {error && (
                    <Alert color="red" title={t('error', 'Error')}>
                        {error}
                    </Alert>
                )}

                <Box>
                    <Button 
                        onClick={handleAccept} 
                        loading={isLoading}
                        fullWidth
                        size="md"
                    >
                        {t('mme.calculator.warningModal.accept', 'I Understand')}
                    </Button>
                </Box>

                <Text size="xs" c="dimmed" style={{ textAlign: 'center' }}>
                    {t('mme.calculator.warningModal.note', 'This message will only appear once.')}
                </Text>
            </Stack>
        </Modal>
    );
};
